import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { useMemo } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "@/assets/logo-novo.png";

const Footer = () => {
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const shouldAnimate = !reduceMotion && !isMobile;

  const particles = useMemo(() => {
    if (!shouldAnimate) return [];
    return Array.from({ length: 10 }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 1,
      x: Math.random() * 100,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 10
    }));
  }, [shouldAnimate]);

  return (
    <footer className="relative bg-gradient-to-b from-background via-background to-black text-white overflow-hidden">
      {shouldAnimate &&
      <>
          <div
          className="absolute -left-48 top-1/4 w-96 h-96 rounded-full hidden md:block animate-slow-pulse"
          style={{ background: "radial-gradient(circle, hsl(var(--primary)/0.1), transparent 70%)" }} />

          {particles.map((particle) =>
        <div
          key={particle.id}
          className="absolute rounded-full bg-primary/10 hidden md:block animate-float-up"
          style={{ width: particle.size, height: particle.size, left: `${particle.x}%`, bottom: 0, animationDuration: `${particle.duration}s`, animationDelay: `${particle.delay}s` }} />
        )}
        </>
      }

      <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-primary/3 md:hidden" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-4">
                <img src={logo} alt="Studio Skandal" className="h-8 w-auto" width="180" height="36" />
              </Link>
              <p className="text-white/50 text-sm leading-relaxed ml-0">
                Wyjątkowa przestrzeń do zdjęć i video.<br />
                400 m² · 3 poziomy · Pełna prywatność.
              </p>
            </div>

            {/* Oferta */}
            <div>
              <span className="text-white font-semibold mb-4 text-sm uppercase tracking-wider border-b border-primary/40 pb-2 inline-block" role="heading" aria-level={2}>
                Oferta
              </span>
              <ul className="space-y-2.5 mt-4">
                {[
                { to: "/wynajem-studia", label: "Wynajem studia" },
                { to: "/sesje", label: "Sesje biznesowe i prywatne" },
                { to: "/cennik", label: "Cennik" }].
                map((link) =>
                <li key={link.to}>
                    <Link to={link.to} className="text-white/40 hover:text-primary transition-colors duration-200 text-sm">
                      {link.label}
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Studio */}
            <div>
              <span className="text-white font-semibold mb-4 text-sm uppercase tracking-wider border-b border-primary/40 pb-2 inline-block" role="heading" aria-level={2}>
                Studio
              </span>
              <ul className="space-y-2.5 mt-4">
              {[
                { to: "/o-miejscu", label: "Studio" },
                { to: "/kontakt", label: "Kontakt" },
                { to: "/polityka-prywatnosci", label: "Polityka prywatności" }].
                map((link) =>
                <li key={link.to}>
                    <Link to={link.to} className="text-white/40 hover:text-primary transition-colors duration-200 text-sm">
                      {link.label}
                    </Link>
                  </li>
                )}
              </ul>
              


            </div>

            {/* Kontakt */}
            <div>
              <span className="text-white font-semibold mb-4 text-sm uppercase tracking-wider border-b border-primary/40 pb-2 inline-block" role="heading" aria-level={2}>
                Kontakt
              </span>
              <ul className="space-y-3 mt-4">
                <li>
                  <a href="mailto:r.wilk@studioskandal.pl" className="flex items-center gap-3 text-white/40 hover:text-primary transition-colors duration-200 text-sm">
                    <Mail className="w-4 h-4 text-primary/60" />
                    r.wilk@studioskandal.pl
                  </a>
                </li>
                <li>
                  <a href="tel:+48601131339" className="flex items-center gap-3 text-white/40 hover:text-primary transition-colors duration-200 text-sm">
                    <Phone className="w-4 h-4 text-primary/60" />
                    +48 601 13 13 39
                  </a>
                </li>
                <li>
                  <a href="https://share.google/ljqWJfHfCd0GNOlez" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-white/40 hover:text-primary transition-colors duration-200 text-sm">
                    <MapPin className="w-4 h-4 text-primary/60 flex-shrink-0 mt-0.5" />
                    <span>Sienkiewicza 4, Warszawa</span>
                  </a>
                </li>
              </ul>
              <div className="flex gap-3 mt-5">
                <a
                  href="https://www.instagram.com/studioskandal/" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-transparent border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/5 hover:border-primary/40 hover:text-primary transition-all duration-300"
                  aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.facebook.com/studioskandal/" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-transparent border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/5 hover:border-primary/40 hover:text-primary transition-all duration-300"
                  aria-label="Facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/@studioskandal9765" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-transparent border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/5 hover:border-primary/40 hover:text-primary transition-all duration-300"
                  aria-label="YouTube">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/><path d="m10 15 5-3-5-3z"/></svg>
                </a>
                <a
                  href="https://www.tiktok.com/@studioskandal" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-transparent border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/5 hover:border-primary/40 hover:text-primary transition-all duration-300"
                  aria-label="TikTok">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.1a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.47z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 py-6">
          <div className="flex flex-col gap-3 items-center text-center">
            <p className="text-white/20 text-xs">
              © {new Date().getFullYear()} Studio Skandal. Wszystkie prawa zastrzeżone.
            </p>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;